import { PlateAddress } from "../../Model/PlateAddress";
import { EPlateAddressAction, PlateAddressAction } from "../actions/PlateAddressAction";
import { FetchStatus } from "./../../Model/FetchStatus";
import { RootState } from "./RootReducer";

type State = {
  status: FetchStatus;
  plateAddressesByModule: { [index: string]: PlateAddress[] };
  plateAddressById: { [index: string]: PlateAddress };
  plateAddresses: PlateAddress[];
};

const initialState: State = {
  plateAddressesByModule: {},
  plateAddressById: {},
  plateAddresses: [],
  status: {
    error: null,
    completed: false,
    pending: false,
  },
};

function processPlateAddress(plateAddresses: PlateAddress[]) {
  let plateAddressesByModule: { [index: string]: PlateAddress[] } = {};
  let plateAddressesById: { [index: string]: PlateAddress } = {};

  for (let plateAddress of plateAddresses) {
    if (plateAddressesByModule[plateAddress.moduleIdentifier] == null) {
      plateAddressesByModule[plateAddress.moduleIdentifier] = [];
    }
    plateAddressesByModule[plateAddress.moduleIdentifier].push(plateAddress);
    plateAddressesById[plateAddress.id] = plateAddress;
  }

  return {
    byModule: plateAddressesByModule,
    byId: plateAddressesById,
  };
}

function updatePlateAddresses(
  plateAddressesById: { [index: string]: PlateAddress },
  updatedPlateAddresses: PlateAddress[]
): PlateAddress[] {
  var result = { ...plateAddressesById };
  for (let i = 0; i < updatedPlateAddresses.length; i++) {
    const updatedPlateAddress = updatedPlateAddresses[i];
    if (updatedPlateAddress) {
      const plateAddress = {
        ...plateAddressesById[updatedPlateAddress.id],
        usedByPlate: updatedPlateAddress.usedByPlate,
        reservedByPlate: updatedPlateAddress.reservedByPlate,
        plateAddressState: updatedPlateAddress.plateAddressState
      };
      result[updatedPlateAddress.id] = plateAddress;
    }
  }

  return Object.values(result);
}

export function plateAddressesReducer(state = initialState, action: PlateAddressAction): State {
  switch (action.type) {
    case EPlateAddressAction.FETCH_PLATEADDRESSES_PENDING:
      return {
        plateAddressesByModule: {},
        plateAddressById: {},
        plateAddresses: [],
        status: {
          error: null,
          completed: false,
          pending: true,
        },
      };
    case EPlateAddressAction.FETCH_PLATEADDRESSES_SUCCESS: {
      if (action.plateAddresses == null) {
        throw new Error("Plate addresses is null");
      }
      const processedPlateAddress = processPlateAddress(action.plateAddresses);
      return {
        plateAddressesByModule: processedPlateAddress.byModule,
        plateAddressById: processedPlateAddress.byId,
        plateAddresses: action.plateAddresses,
        status: {
          error: null,
          completed: true,
          pending: false,
        },
      };
    }
    case EPlateAddressAction.FETCH_PLATEADDRESSES_ERROR:
      return {
        plateAddressesByModule: {},
        plateAddressById: {},
        plateAddresses: [],
        status: {
          error: action.error,
          completed: false,
          pending: false,
        },
      };
    case EPlateAddressAction.UPDATE_PLATEADDRESSES: {
      if (action.updatedPlateAddresses == null) {
        throw new Error("Updated plate addresses is null");
      }
      const newPlateAddresses = updatePlateAddresses(state.plateAddressById, action.updatedPlateAddresses);
      const processedPlateAddress = processPlateAddress(newPlateAddresses);
      return {
        plateAddressesByModule: processedPlateAddress.byModule,
        plateAddressById: processedPlateAddress.byId,
        plateAddresses: newPlateAddresses,
        status: {
          error: null,
          completed: true,
          pending: false,
        },
      };
    }

    default:
      return state;
  }
}

export const getPlateAddressById = (state: RootState): { [index: string]: PlateAddress } =>
  state.plateAddressStore.plateAddressById;
export const getPlateAddresses = (state: RootState): PlateAddress[] => state.plateAddressStore.plateAddresses;
export const getPlateAddressesOnModule = (state: RootState, moduleIdentifier: string): PlateAddress[] =>
  state.plateAddressStore.plateAddressesByModule[moduleIdentifier];
export const getPlateAddressesFetchStatus = (state: RootState): FetchStatus => state.plateAddressStore.status;
